import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Gift, ChevronDown, ChevronUp } from "lucide-react";
import TopBar from "@/components/TopBar";
import StatusBadge from "@/components/StatusBadge";
import { useLanguage } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { categoryLabel } from "@/lib/categories";

const FALLBACK_IMG =
  "https://images.pexels.com/photos/5872179/pexels-photo-5872179.jpeg?auto=compress&cs=tinysrgb&w=900";

function formatDate(iso, lang) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function OfferedGifts() {
  const { code } = useParams();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get(`/public/events/${code}/offered`)
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [code]);

  return (
    <div className="min-h-screen bg-[#FFF9FB] pb-24">
      <TopBar />
      <div className="max-w-3xl mx-auto px-5">
        <button
          onClick={() => navigate(`/event/${code}`)}
          data-testid="offered-back"
          className="inline-flex items-center gap-2 text-sm text-[#5C4664] hover:text-[#2A0E3C]"
        >
          <ArrowLeft className="w-4 h-4" /> {t("product.backToEvent")}
        </button>

        <div className="mt-6 flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#FCE7F3] text-[#D81B8C] flex items-center justify-center">
            <Gift className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C] leading-tight">
              {t("offered.title")}
            </h1>
            <p className="mt-1 text-[#5C4664]">{t("offered.sub")}</p>
          </div>
        </div>

        <div className="mt-10 space-y-3">
          {loading ? (
            <div className="py-16 text-center text-[#5C4664]">{t("common.loading")}</div>
          ) : items.length === 0 ? (
            <div data-testid="offered-empty" className="py-16 text-center text-[#5C4664]">
              {t("offered.noProducts")}
            </div>
          ) : (
            items.map((item) => {
              const reservation = item.active_reservation;
              const isReserved = item.status === "reserve";
              const isOpen = expanded === item.id;
              return (
                <div
                  key={item.id}
                  data-testid={`offered-item-${item.id}`}
                  className="bg-white rounded-2xl border border-[#F2E2EC] overflow-hidden"
                >
                  <button
                    onClick={() => isReserved ? setExpanded(isOpen ? null : item.id) : null}
                    className={`w-full flex items-center gap-4 p-4 text-left ${
                      isReserved ? "hover:bg-[#FCE7F3]/30" : ""
                    }`}
                  >
                    <div className="w-16 h-16 rounded-xl bg-[#FCE7F3] overflow-hidden shrink-0">
                      <img
                        src={item.image_url || FALLBACK_IMG}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C4664]">
                        {categoryLabel(item.category, lang)}
                      </div>
                      <div className="font-display text-base sm:text-lg font-black text-[#2A0E3C] leading-tight truncate">
                        {item.name}
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <StatusBadge status={item.status} />
                        <span className="font-display text-sm font-bold text-[#D81B8C]">
                          ${(item.final_price ?? item.price).toFixed(0)}
                        </span>
                      </div>
                    </div>
                    {isReserved && (
                      <span className="text-[#5C4664]">
                        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </span>
                    )}
                  </button>

                  {isReserved && isOpen && reservation && (
                    <div
                      data-testid={`reservation-detail-${item.id}`}
                      className="px-4 pb-5 -mt-1 pt-3 border-t border-[#F2E2EC] bg-[#FCE7F3]/30"
                    >
                      <div className="text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="text-[#5C4664]">{t("offered.reservedOn")}</span>
                          <span className="text-[#2A0E3C] font-semibold">
                            {formatDate(reservation.created_at, lang)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#5C4664]">{t("offered.expiresOn")}</span>
                          <span className="text-[#2A0E3C] font-semibold">
                            {formatDate(reservation.expires_at, lang)} {t("offered.at")}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
